import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  IconButton,
  Divider,
  Link,
  Button,
  Avatar,
  Stack,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  AttachFile as AttachmentIcon,
  BugReport as BugIcon,
  Image as ImageIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { BugReport } from '../../types/bugReport';

interface BugReportDialogProps {
  open: boolean;
  onClose: () => void;
  bugReport: BugReport | null;
  onEdit?: (bugReport: BugReport) => void;
  onDelete?: (bugReportId: string) => void;
}

export function BugReportDialog({ 
  open, 
  onClose, 
  bugReport, 
  onEdit, 
  onDelete 
}: BugReportDialogProps) {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');

  if (!bugReport) return null;

  const getStatusStyle = (status: BugReport['status']) => {
    switch (status) {
      case 'open':
        return {
          backgroundColor: '#EBF8FF',
          color: '#1E40AF',
          border: '1px solid #BFDBFE'
        };
      case 'in-progress':
        return {
          backgroundColor: '#FEF3C7',
          color: '#D97706',
          border: '1px solid #FDE68A'
        };
      case 'resolved':
        return {
          backgroundColor: '#D1FAE5',
          color: '#059669',
          border: '1px solid #A7F3D0'
        };
      case 'closed':
        return {
          backgroundColor: '#F3F4F6',
          color: '#6B7280',
          border: '1px solid #D1D5DB'
        };
      default:
        return {
          backgroundColor: '#F3F4F6',
          color: '#6B7280',
          border: '1px solid #D1D5DB'
        };
    }
  };

  const getStatusLabel = (status: BugReport['status']): string => {
    switch (status) {
      case 'open':
        return 'AÇIK';
      case 'in-progress':
        return 'DEVAM EDİYOR';
      case 'resolved':
        return 'ÇÖZÜLDÜ';
      case 'closed':
        return 'KAPALI';
      default:
        return status;
    }
  };

  const getPriorityStyle = (priority: BugReport['priority']) => {
    switch (priority) {
      case 'low':
        return {
          backgroundColor: '#F0F9FF',
          color: '#0369A1',
          border: '1px solid #BAE6FD'
        };
      case 'medium':
        return {
          backgroundColor: '#FFFBEB',
          color: '#D97706',
          border: '1px solid #FED7AA'
        };
      case 'high':
        return {
          backgroundColor: '#FEF2F2',
          color: '#DC2626',
          border: '1px solid #FECACA'
        };
      case 'critical':
        return {
          backgroundColor: '#FDF2F8',
          color: '#BE185D',
          border: '1px solid #FBCFE8'
        };
      default:
        return {
          backgroundColor: '#F3F4F6',
          color: '#6B7280',
          border: '1px solid #D1D5DB'
        };
    }
  };

  const getPriorityLabel = (priority: BugReport['priority']): string => {
    switch (priority) {
      case 'low':
        return 'DÜŞÜK ÖNCELİK';
      case 'medium':
        return 'ORTA ÖNCELİK';
      case 'high':
        return 'YÜKSEK ÖNCELİK';
      case 'critical':
        return 'KRİTİK ÖNCELİK';
      default:
        return `${priority} ÖNCELİK`;
    }
  };

  const isImageFile = (url: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    return imageExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  const getFileNameFromUrl = (url: string): string => {
    return url.split('/').pop() || 'ek-dosya';
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
    setImageModalOpen(true);
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setSelectedImageUrl('');
  };

  const handleDelete = () => {
    if (window.confirm('Bu hata raporunu silmek istediğinizden emin misiniz?')) {
      onDelete?.(bugReport.id);
      onClose();
    }
  };

  const handleEdit = () => {
    onEdit?.(bugReport);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            minWidth: { md: 800 }
          }
        }}
      >
        {/* Header with gradient background */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: '#fff',
          p: 3
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <BugIcon sx={{ fontSize: 28, color: '#fff' }} />
                <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: '#fff' }}>
                  Hata Raporu Detayları
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, lineHeight: 1.2, color: '#fff' }}>
                {bugReport.title}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={getStatusLabel(bugReport.status)}
                  sx={{
                    ...getStatusStyle(bugReport.status),
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    background: getStatusStyle(bugReport.status).backgroundColor,
                    color: getStatusStyle(bugReport.status).color,
                    border: getStatusStyle(bugReport.status).border
                  }}
                />
                <Chip
                  label={getPriorityLabel(bugReport.priority)}
                  sx={{
                    ...getPriorityStyle(bugReport.priority),
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    background: getPriorityStyle(bugReport.priority).backgroundColor,
                    color: getPriorityStyle(bugReport.priority).color,
                    border: getPriorityStyle(bugReport.priority).border
                  }}
                />
              </Stack>
            </Box>
            <IconButton 
              onClick={onClose} 
              sx={{ 
                color: '#fff',
                bgcolor: 'rgba(255,255,255,0.15)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            {/* Description Card */}
            <Paper elevation={0} sx={{ p: 0, mb: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <Box sx={{
                background: '#fff',
                p: 2
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0 }}>
                  Açıklama
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#1e293b' }}>
                  {bugReport.description}
                </Typography>
              </Box>
            </Paper>

            {/* Info Grid */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
              gap: 3,
              mb: 3 
            }}>
              {/* Reporter Card */}
              <Paper elevation={0} sx={{ p: 0, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{
                  background: '#fff',
                  p: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0 }}>
                    Raporlayan
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                  <Avatar sx={{ 
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #ffffff 100%)',
                    color: '#1e3a8a',
                    width: 48,
                    height: 48,
                    fontSize: '1.2rem',
                    fontWeight: 600
                  }}>
                    {bugReport.reporter.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {bugReport.reporter}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Created Date Card */}
              <Paper elevation={0} sx={{ p: 0, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{
                  background: '#fff',
                  p: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0 }}>
                    Oluşturulma
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CalendarIcon sx={{ color: '#64748b' }} />
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {new Date(bugReport.createdAt).toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Updated Date Card */}
              <Paper elevation={0} sx={{ p: 0, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{
                  background: '#fff',
                  p: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0 }}>
                    Güncellenme
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CalendarIcon sx={{ color: '#64748b' }} />
                  </Box>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {new Date(bugReport.updatedAt).toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Page URL Card */}
              {bugReport.pageUrl && (
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <LinkIcon sx={{ color: '#64748b' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Sayfa URL
                      </Typography>
                      <Link
                        href={bugReport.pageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        <Typography variant="body1" color="primary" sx={{ fontWeight: 600 }}>
                          Sayfayı Görüntüle
                        </Typography>
                      </Link>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Box>

            {/* Attachments Section */}
            {bugReport.attachments && bugReport.attachments.length > 0 && (
              <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <AttachmentIcon sx={{ color: '#64748b' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Ek Dosyalar ({bugReport.attachments.length})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {bugReport.attachments.map((attachment, index) => {
                    const isImage = isImageFile(attachment);
                    const fileName = getFileNameFromUrl(attachment);
                    
                    if (isImage) {
                      return (
                        <Box 
                          key={index}
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            }
                          }}
                          onClick={() => handleImageClick(attachment)}
                        >
                          <Box
                            sx={{
                              width: 120,
                              height: 120,
                              border: '2px solid #e2e8f0',
                              borderRadius: 2,
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#f8fafc'
                            }}
                          >
                            <img
                              src={attachment}
                              alt={`Ek ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                            <ImageIcon 
                              className="hidden"
                              sx={{ fontSize: 40, color: '#94a3b8' }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            textAlign: 'center', 
                            maxWidth: 120, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            fontWeight: 500
                          }}>
                            {fileName}
                          </Typography>
                        </Box>
                      );
                    } else {
                      return (
                        <Box 
                          key={index}
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Box
                            sx={{
                              width: 120,
                              height: 120,
                              border: '2px solid #e2e8f0',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#f8fafc',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                bgcolor: '#e2e8f0',
                                borderColor: '#cbd5e1'
                              }
                            }}
                            component="a"
                            href={attachment}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                              <AttachmentIcon sx={{ fontSize: 32, color: '#64748b' }} />
                              <DownloadIcon sx={{ fontSize: 20, color: '#64748b' }} />
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            textAlign: 'center', 
                            maxWidth: 120, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            fontWeight: 500
                          }}>
                            {fileName}
                          </Typography>
                        </Box>
                      );
                    }
                  })}
                </Box>
              </Paper>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onEdit && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Düzenle
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Sil
              </Button>
            )}
          </Box>
          <Button
            variant="outlined"
            onClick={onClose}
           
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ position: 'relative', maxWidth: '100%', maxHeight: '90vh' }}>
            <img
              src={selectedImageUrl}
              alt="Büyütülmüş görsel"
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: 8
              }}
            />
            <IconButton
              onClick={handleImageModalClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
} 